import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ReferenceData, Sport, SportConfig } from './Model';
import { restClient } from './RestClient';

interface ReferenceDataContextType {
  referenceData: ReferenceData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  selectedSelectionId: number | null;
  setSelectedSelectionId: (id: number) => void;
  currentSport: Sport;
  currentSportConfig: SportConfig | null;
  currentShareType: 'Team' | 'Player';
  currentPlayerId?: number;
}

const ReferenceDataContext = createContext<ReferenceDataContextType | undefined>(undefined);

interface ReferenceDataProviderProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
}

export const ReferenceDataProvider: React.FC<ReferenceDataProviderProps> = ({
  children,
  isAuthenticated
}) => {
  const [referenceData, setReferenceData] = useState<ReferenceData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSelectionId, setSelectedSelectionId] = useState<number | null>(null);

  const currentSelection = referenceData?.selections.find(s => s.id === selectedSelectionId);
  const currentSport: Sport = currentSelection?.sport ?? 'Football';
  const currentShareType: 'Team' | 'Player' = currentSelection?.shareType ?? 'Team';
  const currentPlayerId = currentSelection?.playerId;
  const currentSportConfig = useMemo(() => {
    if (!referenceData?.sports) return null;
    return referenceData.sports[currentSport] ?? referenceData.sports['Football'] ?? null;
  }, [referenceData, currentSport]);

  const fetchReferenceData = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await restClient.getReferenceData();
    if (result.IsSuccess) {
      setReferenceData(result.Value);
      // Set default selection to first one if not already set
      if (selectedSelectionId === null && result.Value.selections.length > 0) {
        setSelectedSelectionId(result.Value.selections[0].id);
      }
    } else {
      setError(result.Error.description);
      console.error('Failed to load reference data:', result.Error);
    }

    setIsLoading(false);
  }, [isAuthenticated, selectedSelectionId]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  return (
    <ReferenceDataContext.Provider
      value={{
        referenceData,
        isLoading,
        error,
        refetch: fetchReferenceData,
        selectedSelectionId,
        setSelectedSelectionId,
        currentSport,
        currentSportConfig,
        currentShareType,
        currentPlayerId
      }}
    >
      {children}
    </ReferenceDataContext.Provider>
  );
};

export const useReferenceData = (): ReferenceDataContextType => {
  const context = useContext(ReferenceDataContext);
  if (context === undefined) {
    throw new Error('useReferenceData must be used within a ReferenceDataProvider');
  }
  return context;
};
